import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Input, Form, FormProps, Table, Select, DatePicker } from 'antd';

import { useGetGroupBookings } from 'hooks/useGetGroupBookings';

import { PAGE_SIZE_STAYING_GUESTS } from 'services/api/module/report.api';
import { getColumnListGroup } from 'constants/table';
import { getDetailGroupURL } from 'routes/constants';

import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';

import { filterTruthyValues } from 'utils';
import { RangeValueType } from 'services/api/type/common.type';
import 'styles/list-group.scss';

import get from 'lodash/get';
import set from 'lodash/set';

interface FilterOptionsProps {
  state?: string;
  first_arrival?: string;
  last_departure?: string;
  short_code?: string;
  search?: string;
}

function ListGroup() {
  const { t } = useTranslation();

  const STATE_TYPE = [
    { value: '', label: `${t('groupPage.Status.all')}` },
    { value: 'initial', label: `${t('groupPage.Status.initial')}` },
    { value: 'confirmed', label: `${t('groupPage.Status.confirmed')}` },
    { value: 'cancelled', label: `${t('groupPage.Status.cancelled')}` },
    { value: 'completed', label: `${t('groupPage.Status.completed')}` }
  ];

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_STAYING_GUESTS);

  const [filterOptions, setFilterOptions] = useState<FilterOptionsProps>({
    state: ''
  });
  const { data, pagination, isFetching } = useGetGroupBookings(page, filterOptions);

  const handleChangePage = (page: number) => {
    setPage(page);
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    const truthyValues = filterTruthyValues(values);
    const { rangeDate, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const checkInFrom: Dayjs = rangeDate[0];
      const checkInTo: Dayjs = rangeDate[1];
      if (checkInFrom) {
        set(restFilterOptions, 'first_arrival', checkInFrom.format('YYYY-MM-DD'));
      }
      if (checkInTo) {
        set(restFilterOptions, 'last_departure', checkInTo.format('YYYY-MM-DD'));
      }
    }

    if (page !== 1) {
      setPage(1);
    }
    setFilterOptions(restFilterOptions);
  };

  const handleRangeDateChange = (value: RangeValueType | null) => {
    const startDate: Dayjs | null = get(value, '0', null);
    const endDate: Dayjs | null = get(value, '1', null);
    form.setFieldValue('rangeDate', [startDate, endDate]);
  };

  const columnListGroup = useMemo(() => {
    return getColumnListGroup(page, t);
  }, [page, t]);

  useEffect(() => {
    const values = form.getFieldsValue();
    onFinish(values);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="pms-list-group">
      <Card title={t('groupPage.groupList.title')} style={{ width: '100%' }}>
        <Form
          form={form}
          name="list-group-filter-form"
          layout="inline"
          initialValues={{
            search: '',
            state: ''
          }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <div className="pms-list-group__filter">
            <Form.Item name="search" className="m-0" style={{ width: '280px' }}>
              <Input placeholder={t('groupPage.createGroup.groupName')} allowClear />
            </Form.Item>
            <Form.Item name="rangeDate">
              <DatePicker.RangePicker
                className="w-full"
                placeholder={[t('common.time.startDate'), t('common.time.endDate')]}
                format="YYYY-MM-DD"
                onChange={handleRangeDateChange}
              />
            </Form.Item>
            <Form.Item name="state" className="m-0" style={{ width: '110px' }}>
              <Select placeholder={t('groupPage.groupList.Status')}>
                {STATE_TYPE.map(item => {
                  return (
                    <Select.Option key={item.value} value={item.value}>
                      {item.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Button htmlType="submit" className="ant-btn-secondary btn-submit">
              {t('common.actions.submitButton')}
            </Button>
          </div>
        </Form>

        <div className="pms-list-group__table">
          <Table
            rowKey="id"
            loading={isFetching}
            columns={columnListGroup}
            dataSource={data}
            size="small"
            bordered
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            onRow={record => {
              return {
                onClick: () => {
                  navigate(getDetailGroupURL(record.id));
                } // click row
              };
            }}
            scroll={{ x: 1000, y: 'calc(100vh - 400px)' }}
            pagination={{
              current: page,
              position: ['bottomCenter'],
              total: pagination.total,
              pageSize: pageSize,
              locale: { items_per_page: `/ ${t('common.page')}` },
              showTotal: total => `${total} ${t('common.item')}`,
              onChange: (page: number) => handleChangePage(page),
              onShowSizeChange: (current, size) => {
                setPageSize(size);
              }
            }}
          />
        </div>
      </Card>
    </div>
  );
}

export default ListGroup;
